import React, { useEffect, useRef } from 'react';

interface ChartModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * Fullscreen chart viewer. Charts are cramped inside a chat bubble on phones,
 * so the "Expand" affordance opens the same chart here at full viewport size
 * (rotate to landscape for wide diagrams like cash flow).
 *
 * The body is an absolutely-positioned canvas so Recharts' / nivo's responsive
 * containers resolve against a concrete box when asked for height: 100%.
 */
const ChartModal: React.FC<ChartModalProps> = ({ title, onClose, children }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Remember what was focused (the chart's Expand button) so focus can be
    // restored there when the modal closes.
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      // Trap Tab inside the modal so keyboard focus can't reach the chat behind it.
      if (e.key === 'Tab' && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    // Prevent the chat behind the modal from scrolling while it's open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [onClose]);

  return (
    <div ref={modalRef} className="chart-modal" role="dialog" aria-modal="true" aria-label={`${title} — fullscreen`}>
      <div className="chart-modal-header">
        <span className="chart-modal-title">{title}</span>
        <button
          ref={closeRef}
          className="chart-modal-close"
          onClick={onClose}
          aria-label="Close fullscreen chart"
        >
          <span aria-hidden="true">✕</span>
        </button>
      </div>
      <div className="chart-modal-body">
        <div className="chart-modal-canvas">
          {children}
        </div>
      </div>
    </div>
  );
};

export default ChartModal;
